import { type ForwardedRef, forwardRef, memo } from 'react';
import styled, { css } from 'styled-components';

import { ellipsisText, multilineEllipsis } from '@/styles/style';

import type {
  TypoProps,
  TypoSizeType,
  TypoStyleProps,
  TypoType,
  TypoWeight,
} from './types';

export const getFontSize = (type?: TypoType) => {
  switch (type) {
    case 'body0':
      return '18px';
    case 'body1':
      return '16px';
    case 'body2':
      return '14px';
    case 'body3':
      return '12px';
    case 'body4':
      return '11px';
    case 'body5':
      return '10px';
    case 'label0':
      return '18px';
    case 'label1':
      return '16px';
    case 'label2':
      return '14px';
    case 'label3':
      return '12px';
    case 'label4':
      return '11px';
    case 'label5':
      return '10px';
    default:
      return 'inherit';
  }
};

export const getFontSizeBySizeType = (sizeType?: TypoSizeType) => {
  switch (sizeType) {
    case 0:
      return '18px';
    case 1:
      return '16px';
    case 2:
      return '14px';
    case 3:
      return '12px';
    case 4:
      return '11px';
    case 5:
      return '10px';
    default:
      return 'inherit';
  }
};

export const getLineHeightBySizeType = (sizeType?: TypoSizeType) => {
  switch (sizeType) {
    case 0:
      return '28px';
    case 1:
      return '24px';
    case 2:
      return '20px';
    case 3:
      return '18px';
    case 4:
      return '16px';
    case 5:
      return '14px';
    default:
      return 'inherit';
  }
};

export const getLineHeight = (type?: TypoType) => {
  switch (type) {
    case 'body0':
      return '28px';
    case 'body1':
      return '24px';
    case 'body2':
      return '22px';
    case 'body3':
      return '18px';
    case 'body4':
      return '16px';
    case 'body5':
      return '15px';
    default:
      return 1;
  }
};

export const getTypography = (type?: TypoType) => {
  return {
    fontSize: getFontSize(type),
    lineHeight: getLineHeight(type),
  };
};

export const getWeight = (weight?: TypoWeight) => {
  switch (weight) {
    case 'l':
      return 300;
    case 'r':
      return 400;
    case 'm':
      return 500;
    case 'sb':
      return 600;
    case 'b':
      return 700;
    default:
      return 'inherit';
  }
};

export const Paragraph = styled.p<TypoStyleProps>`
  font-size: ${({ type, size }) =>
    typeof size !== 'undefined'
      ? getFontSizeBySizeType(size)
      : getFontSize(type)};
  line-height: ${({ type, size }) =>
    typeof size !== 'undefined'
      ? getLineHeightBySizeType(size)
      : getLineHeight(type)};
  font-weight: ${({ weight }) => getWeight(weight)};
  ${({ color }) =>
    color &&
    css`
      color: ${color};
    `};
  ${(props) =>
    props.multilineEllipsis && multilineEllipsis(props.multilineEllipsis)};
`;

export const Span = styled.span<TypoStyleProps>`
  font-size: ${({ type, size }) =>
    typeof size !== 'undefined'
      ? getFontSizeBySizeType(size)
      : getFontSize(type)};
  line-height: 1.2;
  font-weight: ${({ weight }) => getWeight(weight)};
  ${({ color }) =>
    color &&
    css`
      color: ${color};
    `};
  ${(props) =>
    props.ellipsis &&
    css`
      ${ellipsisText};
      line-height: 1.2;
    `};
`;

export const SpanStyle = (props: TypoStyleProps) => css`
  font-size: ${() =>
    typeof props.size !== 'undefined'
      ? getFontSizeBySizeType(props.size)
      : getFontSize(props.type)};
  line-height: 1.2;
  font-weight: ${() => getWeight(props.weight)};
  color: ${props.color || 'inherit'};
  ${props.ellipsis &&
  css`
    ${ellipsisText};
    line-height: 1.2;
  `};
`;

export const ParagraphStyle = (props: TypoStyleProps) => css`
  font-size: ${() =>
    typeof props.size !== 'undefined'
      ? getFontSizeBySizeType(props.size)
      : getFontSize(props.type)};
  line-height: ${() =>
    typeof props.size !== 'undefined'
      ? getLineHeightBySizeType(props.size)
      : getLineHeight(props.type)};
  font-weight: ${() => getWeight(props.weight)};
  ${props.color &&
  css`
    color: ${props.color};
  `};
  ${() =>
    props.multilineEllipsis && multilineEllipsis(props.multilineEllipsis)};
`;

const Typo = (
  props: TypoProps,
  ref?: ForwardedRef<HTMLSpanElement | HTMLParagraphElement>,
) => {
  if (props.type?.indexOf('label') === -1)
    return (
      <Paragraph
        {...props}
        color={props.color}
        ref={ref as ForwardedRef<HTMLParagraphElement>}
      >
        {props.children}
      </Paragraph>
    );
  // label인 경우 label 태그 반환
  return (
    <Span {...props} color={props.color} ref={ref}>
      {props.children}
    </Span>
  );
};

export default memo(forwardRef(Typo));
